(ns nosco.util.format
  (:require [clojure.string :as string]
            [nosco.i18n.translate :as translate]))

(defn po-locale->base-locale [s]
  (when s
    (string/replace s #"[_-]post$" "")))

(defn platform->browser-locale [s]
  (-> s
      (string/replace "_" "-")
      po-locale->base-locale
      (string/lower-case)
      ;; we use en-gb as the default, since our audience is mainly European
      ;; and would be very confused by the reversed data format in the US
      ;; if we expose proper en_US and en_GB locales, we should of course
      ;; remove this;
      ;; we could perhaps look in navigator.languages to be smarter about this?
      (string/replace "en-us" "en-gb")))

(def platform-locale
  (some->
   (translate/active-locale)
   platform->browser-locale))

;; used for tests
(def ^:dynamic *current-locale* nil)

(defn current-locale []
  (or *current-locale* platform-locale js/undefined))

(def style->options {:time-only #js {:timeStyle "short"}
                     :short-date #js {:dateStyle "short"}
                     :short-datetime #js {:dateStyle "short"
                                          :timeStyle "short"}
                     :medium-date #js {:dateStyle "medium"}
                     :medium-datetime #js {:dateStyle "medium"
                                           :timeStyle "short"}

                     :medium-datetime-detailed #js {:dateStyle "medium"
                                                    :timeStyle "medium"}

                     :long-date #js {:dateStyle "long"}
                     :long-datetime #js {:dateStyle "long"
                                         :timeStyle "short"}

                     ;; Compact date styles for chart axis tick labels
                     :month-year #js {:month "2-digit" :year "numeric"}
                     :short-month-year #js {:month "2-digit" :year "2-digit"}
                     :year-only #js {:year "numeric"}
                     :short-year-only #js {:year "2-digit"}})

(defn date-formatter [locale style]
  (js/Intl.DateTimeFormat. locale
                           (style->options style)))

(defn date-format
  ([d] (date-format d :short-date))
  ([d style]
   (when d
     (let [locale (current-locale)]
       (try
         (.format (date-formatter locale style) d)
         (catch :default e
           (js/console.error "Error formatting date" d (str style) locale)
           (throw (ex-info "Error formatting date" {:date d :style style
                                                    :locale locale} e))))))))

(defn date-relative [future-date past-date]
  (let [seconds (/ (js/Math.floor (- future-date past-date)) 1000)
        minutes (js/Math.floor (/ seconds 60.0))
        hours (js/Math.floor (/ minutes 60.0))
        days (js/Math.floor (/ hours 24.0))
        hours (- hours (* 24 days))
        minutes (- minutes (* 24 60 days) (* 60 hours))
        seconds (js/Math.floor (- seconds (* 24 60 60 days) (* 60 60 hours) (* 60 minutes)))]
    (cond->
     {:hours hours
      :days days}
      (= days 0)
      (assoc :minutes minutes)
      (= days hours 0)
      (assoc :seconds seconds))))

(defn relative-date-refresh-interval
  "Returns the interval in milliseconds at which a relative date should be refreshed."
  [relative-datetime-map]
  (cond
    (:seconds relative-datetime-map) 1000
    (:minutes relative-datetime-map) (* 1000 60)
    (:hours relative-datetime-map) (* 1000 60 60)
    (:days relative-datetime-map) (* 1000 60 60 24)))

(defn- combine-parts->str-compound
  "Given parts as returned from Intl.RelativeTimeFormat, will combine them
   together so that they form a full sentence."
  [parts]
  ;; TODO this assumes that there is a common part (e.g. in western languages, "in", "om" etc)
  ;; that goes in the beginning, but breaks down in Asian languages for two reasons:
  ;; a) the common part goes at the end
  ;; b) it doesn't come as its own part, instead it is embedded in the unit string :/
  ;; leaving as-is for now to discuss with the Rocket Club about the whole thing
  (let [all-parts (into [] cat parts)
        same-part (some->>
                   (frequencies all-parts)
                   (filter (fn [[k v]]
                             (> v 1)))
                   (first)
                   key)]
    (str (:value same-part)
         (->> (for [p parts
                    :when p
                    :let [p (remove #{same-part} p)]]
                (apply str (map :value p)))
              (interpose " ")
              (apply str)))))

(defn format-date-relative
  "Given a map of days, hours, minutes, seconds, will return a string like
   `in 2 hours 20 minutes 15 seconds`. Will skip zero values (except for seconds.)"
  [{:keys [days hours minutes seconds]}]
  (let [locale (current-locale)
        formatter (js/Intl.RelativeTimeFormat. locale #js {:style "long"})
        day-parts (when (> days 0) (js->clj (.formatToParts formatter days "day") :keywordize-keys true))
        hour-parts (when (> hours 0) (js->clj (.formatToParts formatter hours "hour") :keywordize-keys true))
        minute-parts (when (> minutes 0) (js->clj (.formatToParts formatter minutes "minute") :keywordize-keys true))
        second-parts (when seconds (js->clj (.formatToParts formatter seconds "second") :keywordize-keys true))]
    (combine-parts->str-compound [day-parts hour-parts minute-parts second-parts])))

(def ^:dynamic *now*)

(defn time-ago
  "Format a date to show 'time ago' using opinionated rules
   If it's <60s, show 'just now'
   If it's <60m, show minutes
   If it's <24h, show hours
   If it's <7d, show days
   Otherwise show the absolute date formatted with `fallback-style`.
   Doesn't deal with dates in the future. "
  [date {:keys [format-style numeric-style fallback-style]
         :or {format-style "long"
              numeric-style "auto"}}]
  (let [{:keys [days hours minutes seconds]} (date-relative (or *now* (js/Date.)) date)

        locale (current-locale)
        formatter (js/Intl.RelativeTimeFormat. locale #js {:style format-style
                                                           :numeric numeric-style})]
    (cond
      (>= days 7) (date-format date fallback-style)

      (>= days 1) (.format formatter (* -1 days) "day")

      (>= hours 1) (.format formatter (* -1 hours) "hour")

      (>= minutes 1) (.format formatter (* -1 minutes) "minute")

      ;;(>= seconds 1) (.format formatter (* -1 seconds) "second")

      :else (translate/t "just now"))))

(comment

  (date-relative (js/Date. "2023-06-11T05:00:00")
                 (js/Date. "2023-06-11T04:00:00"))

  (date-format (js/Date. "2023-06-01T06:00:00") :medium-date)

  (time-ago (js/Date. "2023-06-16T11:45:00") {:fallback-style :medium-date
                                              :format-style "short"})

  (binding [*current-locale* "fr"]
    (format-date-relative {:days 10 :hours 10})))

(defn platform-number-locale
  "The locale to use when parsing and formatting numbers.
   Different from the platform-locale, since we want users with a
   corporate English language to still see numbers in their native language"
  []
  ;; navigator.language is not useful - it gives you
  ;; the first language in your browsers language settings, which
  ;; is not at all indicative to your regional settings in the OS,
  ;; we use the platform language, with an important change:
  ;; use german-style number formatting
  ;; we could add a user setting for this, together with choosing the
  ;; language, but since we have very few US/UK-based clients, this
  ;; will work for most of them.
  (let [languages (vec (.-languages js/navigator))
        ;; heuristic; if you bothered to set extra languages in your list
        ;; of languages, and one of them isn't english-based, we assume
        ;; that you want to see the numbers in Europe-style.
        ;; TODO find better heuristic :/
        non-english (remove #{"en-GB" "en-US" "en"} languages)]
    (if (and (= "en-gb" (current-locale))
             (seq non-english))
      "en-de"
      (current-locale))))

(def number-formats
  {:default #js {}
   :time #js {:minimumIntegerDigits 2
              :maximumFractionDigits 0}
   :one-digit #js {:maximumFractionDigits 1
                   :minimumFractionDigits 1}
   :two-digits #js {:maximumFractionDigits 2
                    :minimumFractionDigits 2}
   :percent #js {:style "percent"}})

(defn number-formatter [locale style]
  (js/Intl.NumberFormat. locale (number-formats style)))

(defn number-format
  ([x]
   (number-format x :default))
  ([x style]
   (let [locale (platform-number-locale)]
     (try
       (.format (number-formatter locale style) x)
       (catch :default e
         (js/console.error "Error formatting number" x (str style) locale)
         (throw (ex-info "Error formatting number" {:date x :style style
                                                    :locale locale} e)))))))

(defn- find-type [parts type]
  (.-value (first
            (filter (fn [d]
                      (= (.-type d) type)) parts))))

(def valid-number-re
  (let [locale (platform-number-locale)
        parts (-> (js/Intl.NumberFormat. locale)
                  (.formatToParts 12345.6))
        decimal (find-type parts "decimal")]
    (re-pattern (str "[+-]?[0-9]*([" decimal "][0-9]*)?"))))

(def input-number-formatter
  "A number formatter for input fields, doesn't use thousands
       grouping and exposes the max amount of fraction digits."
  (let [locale (platform-number-locale)
        formatter (js/Intl.NumberFormat. locale #js {:useGrouping false
                                                     :maximumFractionDigits 20})]
    formatter))

(defn format-number-for-input [x]
  (str (when x
         (.format input-number-formatter x))))

;; modified approach from https://observablehq.com/@mbostock/localized-number-parsing
(defn locale-number-parse [s]
  (let [parts (-> input-number-formatter
                  (.formatToParts 12345.6))
        decimal (find-type parts "decimal")
        re-decimal (re-pattern (str "[" decimal "]"))]
    (-> s
        (string/trim)
        (string/replace re-decimal ".")
        (parse-double))))
